// История тренировок: по датам, постранично по 30, фильтр по шаблону и упражнению.

import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, PageSpinner, Select } from '@/components/ui'
import { formatDuration, formatMonthYear, formatNumber, formatTime, toDate } from '@/lib/format'

import { useExercises, useSessions, useTemplates, type SessionSummary } from './api'

const WEEKDAYS_SHORT = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб']

function monthKey(iso: string): string {
  const d = toDate(iso)!
  return `${d.getFullYear()}-${d.getMonth()}`
}

function SessionRow({ s }: { s: SessionSummary }) {
  const d = toDate(s.started_at)!
  return (
    <li>
      <Link
        to={`/workouts/${s.id}`}
        className="flex items-center gap-4 px-4 py-3 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent"
      >
        <div className="w-12 shrink-0 text-center">
          <div className="text-lg font-semibold tabular-nums leading-tight text-fg">{d.getDate()}</div>
          <div className="text-xs text-muted">{WEEKDAYS_SHORT[d.getDay()]}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-fg">{s.template_name ?? 'Тренировка'}</span>
            <span className="text-xs tabular-nums text-muted">{formatTime(s.started_at)}</span>
            {s.ended_at === null ? <Badge tone="accent">идёт</Badge> : null}
            {s.auto_closed ? <Badge>закрыта автоматически</Badge> : null}
            {s.pr_count > 0 ? <Badge tone="warning">рекорд × {s.pr_count}</Badge> : null}
          </div>
          <p className="mt-0.5 truncate text-sm text-muted">
            {s.exercise_names.length ? s.exercise_names.join(', ') : 'Без подходов'}
          </p>
        </div>
        <div className="hidden shrink-0 text-right text-sm tabular-nums sm:block">
          <div className="text-fg">{s.volume_kg > 0 ? `${formatNumber(s.volume_kg, { digits: 0 })} кг` : '—'}</div>
          <div className="text-xs text-muted">
            {formatDuration(s.duration_s)} · {s.sets_done} подх.
          </div>
        </div>
      </Link>
    </li>
  )
}

export function HistoryPage() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const templateId = params.get('template_id') ?? ''
  const exerciseId = params.get('exercise_id') ?? ''
  const filters = { template_id: templateId || undefined, exercise_id: exerciseId || undefined }
  const sessions = useSessions(filters)
  const templates = useTemplates(true)
  const exercises = useExercises('', '', true)

  const activeFilters = (templateId ? 1 : 0) + (exerciseId ? 1 : 0)
  // На телефоне фильтры свёрнуты в кнопку (раскрыты, если какой-то выбран); с sm — всегда видны.
  const [filtersOpen, setFiltersOpen] = useState(activeFilters > 0)

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const items = sessions.data?.pages.flatMap((p) => p.items) ?? []
  const groups: { key: string; label: string; items: SessionSummary[] }[] = []
  for (const item of items) {
    const key = monthKey(item.started_at)
    const last = groups.at(-1)
    if (last && last.key === key) last.items.push(item)
    else groups.push({ key, label: formatMonthYear(item.started_at), items: [item] })
  }

  return (
    <>
      <PageHeader
        title="Тренировки"
        description="История силовых тренировок."
        actions={
          <>
            <Button variant="primary" icon="plus" onClick={() => navigate('/workouts/new')}>
              Внести тренировку
            </Button>
            <Button
              icon="filter"
              className="sm:hidden"
              aria-expanded={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
            >
              {activeFilters > 0 ? `Фильтр · ${activeFilters}` : 'Фильтр'}
            </Button>
          </>
        }
      />
      <Card className={filtersOpen ? undefined : 'max-sm:hidden'}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Шаблон"
            placeholder="Все шаблоны"
            value={templateId}
            onChange={(e) => setFilter('template_id', e.target.value)}
            options={(templates.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          />
          <Select
            label="Упражнение"
            placeholder="Все упражнения"
            value={exerciseId}
            onChange={(e) => setFilter('exercise_id', e.target.value)}
            options={(exercises.data ?? []).map((ex) => ({ value: ex.id, label: ex.name }))}
          />
        </div>
      </Card>
      <div className="mt-4 grid gap-4">
        {sessions.isPending ? (
          <PageSpinner />
        ) : sessions.error ? (
          <ErrorText error={sessions.error} />
        ) : items.length === 0 ? (
          <Card>
            <EmptyState
              icon="history"
              title={templateId || exerciseId ? 'Таких тренировок нет' : 'Тренировок пока нет'}
              description="Начни тренировку в зале или внеси прошедшую задним числом."
            />
          </Card>
        ) : (
          groups.map((group) => (
            <Card key={group.key} title={group.label} flush>
              <ul className="divide-y divide-border">
                {group.items.map((s) => (
                  <SessionRow key={s.id} s={s} />
                ))}
              </ul>
            </Card>
          ))
        )}
        {sessions.hasNextPage ? (
          <div className="flex justify-center">
            <Button loading={sessions.isFetchingNextPage} onClick={() => sessions.fetchNextPage()}>
              Показать ещё
            </Button>
          </div>
        ) : null}
      </div>
    </>
  )
}
